'use client'

import { UsePromptFormReturn } from "@/lib/types/prompt-area-types"
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form"
import { AnimatedButton } from "@/components/ui/animated-button"
import { SignUpCta } from "@/components/auth/sign-up-cta"
import { useSimpleFileUpload } from "@/hooks/useSimpleFileUpload"
import { ContentFile } from "@/lib/types/file-types"
import { toast } from "sonner"
import { useEffect, useRef, useState } from "react"
import { useDragDrop } from "@/components/providers/drag-drop-provider"

export function PromptAreaFileUpload({
    promptForm,
}: {
    promptForm: UsePromptFormReturn
}) {


    const [isPopoverOpen, setIsPopoverOpen] = useState(false)
    const [isBrowserOpen, setIsBrowserOpen] = useState(false)

    const { setOnFilesDropped, setIsDragActive, isDragActive } = useDragDrop()

    const { openFileBrowser, uploadFiles } = useSimpleFileUpload({
        endpoint: 'fileUploader',
        maxFiles: 10,
        onUploadStart: (file) => {
            promptForm.setIsUploading(true)
            const current = (promptForm.form.getValues as any)('files') || []
            ;(promptForm.form.setValue as any)('files', [...current, file])
        },
        onUploadProgress: (file) => {
            const current = (promptForm.form.getValues as any)('files') || []
            const updated = current.map((f: any) => f.id === file.id ? file : f)
            ;(promptForm.form.setValue as any)('files', updated)
        },
        onUploadComplete: (file) => {
            const current = (promptForm.form.getValues as any)('files') || []
            const updated = current.map((f: any) => f.id === file.id ? file : f)
            ;(promptForm.form.setValue as any)('files', updated)
            promptForm.setIsUploading(false)
        },
        onUploadError: (file) => {
            const current = (promptForm.form.getValues as any)('files') || []
            const updated = current.map((f: any) => f.id === file.id ? file : f)
            ;(promptForm.form.setValue as any)('files', updated)
            promptForm.setIsUploading(false)
        },
    })

    // Route global drops to this uploader (attachments) without causing update loops
    const uploadFilesRef = useRef(uploadFiles)
    useEffect(() => { uploadFilesRef.current = uploadFiles }, [uploadFiles])
    useEffect(() => {
        const handler = (files: File[]) => {
            setIsDragActive(false)
            uploadFilesRef.current(files)
        }
        setOnFilesDropped(handler)
        return () => setOnFilesDropped(() => {})
    }, [setOnFilesDropped, setIsDragActive])

    // Mirror provider drag state into form state so overlay can respond
    useEffect(() => {
        promptForm.setIsDragActive(isDragActive)
    }, [isDragActive])

    const handleDirectUpload = () => {
        openFileBrowser()
        setIsPopoverOpen(false)
    }

    const handleBrowseFiles = () => {
        setIsPopoverOpen(false)
        setIsBrowserOpen(true)
    }

    const handleBrowserSelect = (selectedFiles: ContentFile[]) => {
        // Add selected files to the form
        const currentFiles = promptForm.form.getValues('files') || []
        const newFiles = [...currentFiles, ...selectedFiles]
        promptForm.form.setValue('files', newFiles)
        
        setIsBrowserOpen(false)
    }

    const handlePopoverOpenChange = (open: boolean) => {
        if (!promptForm.userContextValue) {
            toast.error('Please log in to upload files')
            return
        }
        setIsPopoverOpen(open)
    }



    return (
        <FormField
            control={promptForm.form.control}
            name="files"
            render={({ field }) => (
                <FormItem>
                    <FormControl>
                        <AnimatedButton
                lottieIcon="paperclip"
                type="button"
                className="h-8 w-8 rounded-full p-0 bg-transparent hover:bg-transparent hover:cursor-pointer flex items-center justify-center"
                disabled={promptForm.isUploading}
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()

                    if (!promptForm.isUserAuthenticated) {
                        toast.error('Please log in to upload files', {
                            duration: 4000,
                            action: <SignUpCta variant="toast" />,
                            className: "flex flex-row w-full justify-between bg-transparent"
                        })
                        return
                    }
                    handleDirectUpload()
                }}
            />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    )
}